import React from 'react';
import {
  DecoratorNode,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

import { legalEntityClasses } from '../config/calendarTheme';

/**
 * Bill reference node data structure
 */
export interface BillReferenceData {
  billId: string;
  displayName: string;
  amount: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue';
}

/**
 * Serialized bill reference node for JSON storage
 */
export interface SerializedBillReferenceNode extends Spread<
  {
    billData: BillReferenceData;
    type: 'bill-reference';
    version: 1;
  },
  SerializedLexicalNode
> {}

/**
 * Custom Lexical node for $bill references
 */
export class BillReferenceNode extends DecoratorNode<React.JSX.Element> {
  __billData: BillReferenceData;

  static getType(): string {
    return 'bill-reference';
  }

  static clone(node: BillReferenceNode): BillReferenceNode {
    return new BillReferenceNode(node.__billData, node.__key);
  }

  constructor(billData: BillReferenceData, key?: NodeKey) {
    super(key);
    this.__billData = billData;
  }

  exportJSON(): SerializedBillReferenceNode {
    return {
      billData: this.__billData,
      type: 'bill-reference',
      version: 1,
    };
  }

  static importJSON(serializedNode: SerializedBillReferenceNode): BillReferenceNode {
    const { billData } = serializedNode;
    return $createBillReferenceNode(billData);
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = legalEntityClasses.bill;
    return span;
  }

  updateDOM(): false {
    return false;
  }

  decorate(): React.JSX.Element {
    return (
      <span
        className={legalEntityClasses.bill}
        title={`Bill: ${this.__billData.displayName} - $${this.__billData.amount}`}
      >
        💰 ${this.__billData.displayName}
      </span>
    );
  }

  getTextContent(): string {
    return `$${this.__billData.displayName}`;
  }

  isImmutable(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }
}

export function $createBillReferenceNode(billData: BillReferenceData): BillReferenceNode {
  return new BillReferenceNode(billData);
}

export function $isBillReferenceNode(node: LexicalNode | null | undefined): node is BillReferenceNode {
  return node instanceof BillReferenceNode;
} 